import { Injectable } from '@angular/core';
import { ClassStudentService } from '@examdojo/class';
import { ErrorHandlerService } from '@examdojo/error-handling';
import { UserQuery } from '@examdojo/user';
import { assertNonNullable } from '@examdojo/core/util/assert';
import kebabCase from 'lodash/kebabCase';
import { finalize, tap } from 'rxjs';
import { StudentClassAssignmentService } from '../student-class-assignment/student-class-assignment.service';
import { StudentClassHttpService } from './student-class-http.service';
import { StudentClassHttpModel, StudentClassStoreModel } from './student-class.model';
import { StudentClassQuery } from './student-class.query';
import { StudentClassStore } from './student-class.store';

@Injectable({ providedIn: 'root' })
export class StudentClassService {
  constructor(
    private readonly store: StudentClassStore,
    private readonly http: StudentClassHttpService,
    private readonly errorHandler: ErrorHandlerService,
    private readonly query: StudentClassQuery,
    private readonly studentClassAssignmentService: StudentClassAssignmentService,
    private readonly classStudentService: ClassStudentService,
    private readonly userQuery: UserQuery,
  ) {}

  resetStore() {
    this.store.reset();
    this.studentClassAssignmentService.resetStore();
    this.classStudentService.resetStore();
  }

  fetchAllClasses() {
    this.store.setLoading(true);

    return this.http.fetchAllFromView().pipe(
      this.errorHandler.setHttpErrorMetadata({ entity: 'student_class', action: 'fetch' }),
      tap((classes) => this.store.set(classes.map((c) => this.mapToStoreModel(c)))),
      finalize(() => this.store.setLoading(false)),
    );
  }

  fetchClasses(syllabusId: number, courseLevel: string | null) {
    const hasExistingData = this.query.getCount() > 0;

    if (!hasExistingData) {
      this.store.setLoading(true);
    }

    return this.http.fetchBySyllabusContext(syllabusId, courseLevel).pipe(
      this.errorHandler.setHttpErrorMetadata({ entity: 'student_class', action: 'fetch' }),
      tap((classes) => this.store.set(classes.map((c) => this.mapToStoreModel(c)))),
      finalize(() => this.store.setLoading(false)),
    );
  }

  fetchClassById(id: string) {
    return this.http
      .fetchById(id)
      .pipe(this.errorHandler.setHttpErrorMetadata({ entity: 'student_class', action: 'fetch' }));
  }

  addClassToStore(httpModel: StudentClassHttpModel): StudentClassStoreModel {
    const model = this.mapToStoreModel(httpModel);
    this.store.upsert(model.id, model);
    return model;
  }

  lookupClass(code: string) {
    return this.http
      .lookupClassByEnrollmentCode(code)
      .pipe(this.errorHandler.setHttpErrorMetadata({ entity: 'student_class', action: 'fetch' }));
  }

  joinClass(code: string) {
    return this.http
      .joinClassByEnrollmentCode(code)
      .pipe(this.errorHandler.setHttpErrorMetadata({ entity: 'student_class', action: 'update' }));
  }

  leaveClass() {
    const classId = this.query.getActiveId();
    assertNonNullable(classId, 'Active class not found');

    const userId = this.userQuery.getActiveId();
    assertNonNullable(userId, 'User ID not found');

    return this.classStudentService.removeStudent(classId, userId).pipe(tap(() => this.store.delete(classId)));
  }

  setActive(id: string | null) {
    this.store.setActive(id);
  }

  setActiveBySlug(slug: string): boolean {
    const classEntity = this.query.getAllUIEntities().find((c) => c.slug === slug);
    if (!classEntity) {
      return false;
    }
    this.store.setActive(classEntity.id);
    return true;
  }

  buildSlug(name: string, id: string): string {
    return `${kebabCase(name)}-${id.slice(0, 8)}`;
  }

  private mapToStoreModel(httpModel: StudentClassHttpModel): StudentClassStoreModel {
    const id = httpModel.id ?? '';
    const name = httpModel.name ?? '';
    return {
      ...httpModel,
      id,
      name,
      slug: this.buildSlug(name, id),
    };
  }
}
